import { useState, useEffect } from "react";
import { 
  Activity, CheckCircle2, XCircle, Clock, Zap, Search, 
  RefreshCw, MessageSquare, Phone, Send, Filter, ArrowUpRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import API from "../api/api01";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../ui/utils";

interface WebhookLog {
  id: string;
  type: 'sms' | 'whatsapp' | 'autodialer' | 'unknown';
  identifier: string;
  status: string;
  timestamp: string;
  campaign_name: string;
  error: string;
}

interface WebhookReportsProps {
  campaignId?: string;
  campaignType?: string;
  isEmbedded?: boolean;
}

export default function WebhookReports({ campaignId, campaignType, isEmbedded = false }: WebhookReportsProps) {
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(campaignType || "all");
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchLogs = async () => {
    try {
      const url = campaignId 
        ? `/webhooks/reports/?campaign_id=${campaignId}`
        : "/webhooks/reports/";
      const res = await API.get(url);
      setLogs(res.data);
    } catch (err) {
      console.error("Failed to fetch webhook logs", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    let interval: any;
    if (autoRefresh) {
      interval = setInterval(fetchLogs, 5000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, campaignId]);

  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.identifier.includes(search) || log.campaign_name.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'all' || log.type === filter;
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: logs.length,
    delivered: logs.filter(l => ['delivered', 'Completed', 'answered'].includes(l.status)).length,
    failed: logs.filter(l => ['failed', 'Failed', 'rejected'].includes(l.status)).length,
  };

  const getStatusStyle = (status: string) => {
    const s = status.toLowerCase();
    if (['delivered', 'completed', 'answered', 'read'].some(v => s.includes(v))) {
      return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-500/20";
    }
    if (['failed', 'rejected', 'error', 'busy'].some(v => s.includes(v))) {
      return "bg-rose-500/10 text-rose-600 dark:text-rose-500 border-rose-500/20";
    }
    return "bg-amber-500/10 text-amber-600 dark:text-amber-500 border-amber-500/20";
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'sms': return <Send className="h-4 w-4 text-amber-500" />;
      case 'whatsapp': return <MessageSquare className="h-4 w-4 text-emerald-500" />;
      case 'autodialer': return <Phone className="h-4 w-4 text-blue-500" />;
      default: return <Activity className="h-4 w-4 text-slate-500" />;
    }
  };

  const content = (
    <div className={cn("space-y-6", !isEmbedded ? "max-w-[1400px] mx-auto py-8 px-4" : "p-6")}>
      {!isEmbedded && (
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-violet-600 flex items-center justify-center shadow-lg shadow-violet-500/20 ring-4 ring-violet-500/10">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white uppercase italic">
                Live <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-indigo-600 dark:from-violet-400 dark:to-indigo-400">Webhook</span> Streams
              </h1>
            </div>
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.2em] ml-1">
              Real-time delivery orchestration monitoring
            </p>
          </div>

          <div className="flex items-center gap-3 bg-white dark:bg-slate-900/50 p-1.5 rounded-2xl border border-slate-200 dark:border-slate-800/50 backdrop-blur-xl">
             <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={cn(
                  "h-10 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                  autoRefresh ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20" : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                )}
             >
                <div className={cn("h-2 w-2 rounded-full mr-2", autoRefresh ? "bg-emerald-500 animate-pulse" : "bg-slate-600")} />
                {autoRefresh ? "Live Syncing" : "Sync Paused"}
             </Button>
             <Button 
                variant="outline" 
                size="icon" 
                onClick={fetchLogs}
                className="h-10 w-10 rounded-xl border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900/50 hover:bg-slate-200 dark:hover:bg-slate-800"
             >
                <RefreshCw className={cn("h-4 w-4 text-violet-600 dark:text-violet-400", loading && "animate-spin")} />
             </Button>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: "Incoming Signals", value: stats.total, icon: Activity, color: "violet", sub: "Events handled" },
          { label: "Successful Hits", value: stats.delivered, icon: CheckCircle2, color: "emerald", sub: "Positive status" },
          { label: "Failed Signals", value: stats.failed, icon: XCircle, color: "rose", sub: "Error reports" },
        ].map((s, idx) => (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            key={s.label}
            className="group relative bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800/50 p-6 rounded-3xl overflow-hidden hover:border-violet-500/30 transition-all duration-500"
          >
            <div className={cn("absolute top-0 right-0 w-32 h-32 opacity-[0.03] -mr-8 -mt-8 rounded-full", `bg-${s.color}-500`)} />
            <div className="flex items-center justify-between mb-4">
              <div className={cn("p-3 rounded-2xl", `bg-${s.color}-500/10`, `text-${s.color}-600 dark:text-${s.color}-400`)}>
                <s.icon className="h-6 w-6" />
              </div>
              <Badge variant="outline" className="border-slate-200 dark:border-slate-800 text-[9px] font-black uppercase tracking-tighter opacity-50 text-slate-900 dark:text-slate-200">Realtime</Badge>
            </div>
            <div className="space-y-1">
              <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{s.label}</p>
              <h3 className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter italic">{s.value}</h3>
              <p className="text-[10px] text-slate-500 dark:text-slate-600 font-bold uppercase">{s.sub}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Main Stream Area */}
      <Card className={cn("rounded-[2.5rem] border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 backdrop-blur-2xl shadow-2xl overflow-hidden", isEmbedded && "rounded-3xl shadow-none border-none")}>
        <CardHeader className="p-8 border-b border-slate-200 dark:border-slate-800/50 bg-slate-50 dark:bg-slate-900/20">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div>
              <CardTitle className="text-xl font-black text-slate-900 dark:text-white uppercase italic flex items-center gap-3">
                <Activity className="h-5 w-5 text-violet-600 dark:text-violet-500" />
                Live Event Stream
              </CardTitle>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">
                {campaignId ? `Monitoring callbacks for campaign: ${campaignId}` : "Monitoring incoming API callbacks from all providers"}
              </p>
            </div>
            
            <div className="flex items-center gap-4 w-full lg:w-auto">
              <div className="relative flex-1 lg:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-slate-600" />
                <Input 
                  placeholder="Filter by identifier..."
                  className="pl-10 h-11 bg-slate-100 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800 rounded-xl text-[12px] text-slate-900 dark:text-white focus:ring-violet-500/20"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {!campaignType && (
                <div className="flex p-1 bg-slate-100 dark:bg-slate-950/50 rounded-xl border border-slate-200 dark:border-slate-800">
                  {['all', 'sms', 'whatsapp', 'autodialer'].map((f) => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={cn(
                        "px-4 h-9 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all",
                        filter === f ? "bg-violet-600 text-white shadow-lg shadow-violet-600/20" : "text-slate-500 hover:text-slate-300"
                      )}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className={cn("h-[600px]", isEmbedded && "h-[400px]")}>
            <Table>
              <TableHeader className="bg-slate-50/80 dark:bg-slate-950/30 sticky top-0 z-10 backdrop-blur-md">
                <TableRow className="border-slate-200 dark:border-slate-800">
                  <TableHead className="px-8 font-black text-[11px] uppercase tracking-widest text-slate-500">Source</TableHead>
                  <TableHead className="font-black text-[11px] uppercase tracking-widest text-slate-500">Identity</TableHead>
                  <TableHead className="font-black text-[11px] uppercase tracking-widest text-slate-500">Status</TableHead>
                  {!campaignId && <TableHead className="font-black text-[11px] uppercase tracking-widest text-slate-500">Campaign</TableHead>}
                  <TableHead className="px-8 font-black text-[11px] uppercase tracking-widest text-slate-500 text-right">Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <AnimatePresence mode="popLayout">
                  {filteredLogs.length > 0 ? (
                    filteredLogs.map((log, i) => (
                      <motion.tr
                        layout
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        key={log.id}
                        className="group border-b border-slate-100 dark:border-slate-800/40 hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-all"
                      >
                        <TableCell className="px-8 py-5">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-xl bg-slate-100 dark:bg-slate-950 flex items-center justify-center border border-slate-200 dark:border-slate-800 group-hover:border-violet-500/30 group-hover:scale-110 transition-all">
                              {getTypeIcon(log.type)}
                            </div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">{log.type}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm font-black text-slate-900 dark:text-white italic tracking-tight">{log.identifier}</p>
                          {log.error && <p className="text-[9px] text-rose-600 dark:text-rose-500 font-bold uppercase truncate max-w-[200px] mt-1">{log.error}</p>}
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("px-3 py-1 font-black text-[9px] uppercase tracking-widest border", getStatusStyle(log.status))}>
                            {log.status}
                          </Badge>
                        </TableCell>
                        {!campaignId && (
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase">{log.campaign_name}</span>
                              <ArrowUpRight className="h-3 w-3 text-slate-400 dark:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </TableCell>
                        )}
                        <TableCell className="px-8 text-right">
                          <div className="flex flex-col items-end">
                            <span className="text-xs font-black text-slate-900 dark:text-white italic">{new Date(log.timestamp).toLocaleTimeString()}</span>
                            <span className="text-[9px] font-bold text-slate-500 dark:text-slate-600 uppercase">{new Date(log.timestamp).toLocaleDateString()}</span>
                          </div>
                        </TableCell>
                      </motion.tr>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="h-64 text-center">
                        <div className="flex flex-col items-center gap-4 text-slate-400 dark:text-slate-600">
                          <Clock className="h-12 w-12 opacity-20" />
                          <p className="font-black uppercase tracking-widest text-xs">Awaiting incoming signals...</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );

  if (isEmbedded) return content;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617] p-4 lg:p-8 text-slate-900 dark:text-slate-200">
      {content}
    </div>
  );
}
